import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ColorDot } from 'spw-components/worksheet/styles';
import {
  IProject,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  SPWButtonIconAndText,
  SPWMainButton,
  SPWOutlineBlackButton,
} from 'spw-shared/globalComponents/buttons';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_SPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { updateProjectSpaceType } from 'spw-redux/modules/space_types/actions';
import DraggablePopover from 'spw-components/dialogs/draggable-popover';
import { OutlinedSpaceIcon } from 'spw-projects/components/space-type-icons';
import { getColor } from 'spw-shared/utils';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';

const ICONS_SECTION_HEIGHT = 312;

const categoryIconsLists: { [key: string]: string[] } = {
  [INDIVIDUAL_SPACES_CATEGORY]: [
    'custom-workstation-default',
    'workstation-default',
    'hotel-desk-default',
    'desk-chair',
    'custom-private-office-default',
    'private-office-default',
    'iw-custom-subcategory-default',
    'workstation-2',
    'shared-private-office-default',
    'executive-private-office-default',
    'shared-private-office-2',
    'workstation-3',
    'iw-category-default',
    'iw-category-3',
    'iw-category-2',
    'headcount',
  ],
  [MEETING_SPACES_CATEGORY]: [
    'meeting-chair',
    'meeting-2',
    'meeting-3',
    'phone-default',
    'training-default',
    'open-collab-default',
    'custom-meeting-default',
    'meeting-category-2',
    'people',
    'huddle-default',
    'sm-conf-default',
    'meeting-category-default',
    'med-conf-default',
    'lg-conf-default',
    'xl-conf-default',
    'multi-purpose-default',
  ],
  [AMENITY_SPACES_CATEGORY]: [
    'custom-amenity-default',
    'break-room-2',
    'pantry-default',
    'break-room-default',
    'art-room',
    'wellness-default',
    'music-room',
    'bike-room',
    'game-room-default',
    'amenity-category-3',
    'mothers-room-default',
    'fitness-default',
    'library-default',
    'cafe-default',
    'amenity-category-default',
    'meditation-default',
  ],
  [SUPPORT_SPACES_CATEGORY]: [
    'mail-room',
    'it-tech-bar-default',
    'coat-closet-default',
    'reception-default',
    'custom-support-default',
    'mdf-default',
    'idf-default',
    'it-storage-default',
    'file-cabinets-default',
    'storage-default',
    'lockers-default',
    'copy-print-default',
    'satellite-copy-default',
    'support-category-default',
    'janitorial-default',
    'ada-bathroom-default',
  ],
};

const Disclaimer = styled.div`
  && {
    font-weight: 400;
    padding: 8px 16px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const Header = styled.div``;

const SpaceTypeCategoryIcon = styled.img`
  && {
    width: 32px;
    height: 32px;
  }
`;

const STCButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px 8px 4px 8px;
  cursor: grab;
`;

const ScrollContent = styled.div`
  max-height: 346px;
  padding-top: 8px;
  scroll-y: auto;
  overflow: auto;
`;

const IconGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const Footer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
`;

interface IProps {
  project: IProject;
  anchorEl: Element | null;
  onClose: () => void;
}

const IconSelectionSectionContainer = styled.div`
  padding: 0 16px;
`;

const IconSelectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  height: 24px;
  align-items: center;
`;

const HeaderText = styled.span`
  font-family: Roboto;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
`;

// Memoized to avoid jittery behavior while animating the Grow component in or out
const IconSelectionSection = React.memo(
  ({
    spaceTypeCategory: { name, defaultSpaceTypeCategory },
    anchorEl,
    selectedIconName,
    onSelectIcon,
  }: {
    spaceTypeCategory: IProjectSpaceTypeCategory;
    anchorEl: Element;
    selectedIconName: string;
    onSelectIcon: (iconName: string) => void;
  }) => {
    const projectColors = useSelector(selectProjectColors);

    return (
      <IconSelectionSectionContainer>
        <IconSelectionHeader>
          <ColorDot
            style={{ margin: '0 8px 0 4px' }}
            color={getColor(defaultSpaceTypeCategory.category, projectColors)}
          />
          <HeaderText>{name}</HeaderText>
        </IconSelectionHeader>
        <IconGrid>
          {categoryIconsLists[defaultSpaceTypeCategory.category].map(
            (iconName) => (
              <OutlinedSpaceIcon
                key={`icon-${iconName}`}
                disabled={
                  iconName ===
                  anchorEl?.attributes.getNamedItem('data-icon-name')?.value
                }
                active={iconName === selectedIconName}
                onClick={() => onSelectIcon(iconName)}
                name={iconName}
              />
            )
          )}
        </IconGrid>
      </IconSelectionSectionContainer>
    );
  },
  (prevProps, nextProps) => prevProps === nextProps || !nextProps.anchorEl
);

const CategoryAnchorButton = (
  spaceTypeCategory: IProjectSpaceTypeCategory,
  color: string,
  scrollToIconSection: () => void
) => {
  const { id, name, icon } = spaceTypeCategory;

  return (
    <ColorDot
      key={`dot-${id}`}
      color={color}
      className="add-margin large-button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={scrollToIconSection}
    >
      <SpaceTypeCategoryIcon src={`/images/${icon}.svg`} alt={`${name}-icon`} />
    </ColorDot>
  );
};

const ImageSelectorPopover: React.FC<IProps> = ({
  anchorEl,
  onClose,
  project,
}) => {
  const dispatch = useDispatch();
  const { spaceTypeCategories } = project;

  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [disableRestoreBtn, setDisableRestoreBtn] = useState<boolean>(false);

  const scrollableAreaRef = useRef(null);
  const queuedJump = useRef<number | null>(null);
  const projectColors = useSelector(selectProjectColors);

  const curriedScrollTo = (
    x: number,
    y: number,
    behavior: 'smooth' | 'auto' = 'smooth'
  ) => () => {
    if (scrollableAreaRef)
      scrollableAreaRef.current.scrollTo({
        behavior,
        left: x,
        top: y,
      });
  };

  useEffect(() => {
    setDisableRestoreBtn(
      !anchorEl?.attributes.getNamedItem('data-default-icon-name') ||
        anchorEl?.attributes.getNamedItem('data-default-icon-name')?.value ===
          anchorEl?.attributes.getNamedItem('data-icon-name')?.value ||
        selectedIcon ===
          anchorEl?.attributes.getNamedItem('data-default-icon-name')?.value
    );
  }, [anchorEl, selectedIcon]);

  useEffect(() => {
    if (anchorEl) {
      anchorEl.classList.add('selected');

      const iconsSectionIndex = spaceTypeCategories.findIndex(
        (spaceTypeCategory) =>
          String(spaceTypeCategory?.id) ===
          anchorEl.attributes.getNamedItem(
            'data-top-level-project-space-type-category-id'
          )?.value
      );

      if (iconsSectionIndex > -1) {
        queuedJump.current = iconsSectionIndex;
      }
    }
  }, [anchorEl, spaceTypeCategories]);

  const handleOnCancel = () => {
    if (anchorEl) anchorEl.classList.remove('selected');
    onClose();
    setSelectedIcon(null);
  };

  const handleOnSave = () => {
    const projectSpaceTypeId = parseInt(
      anchorEl.attributes.getNamedItem('data-project-space-type-id')?.value,
      10
    );

    const spaceTypeId =
      parseInt(
        anchorEl.attributes.getNamedItem('data-space-type-id')?.value,
        10
      ) || undefined;

    const categoryId = parseInt(
      anchorEl.attributes.getNamedItem('data-project-space-type-category-id')
        ?.value,
      10
    );

    dispatch(
      updateProjectSpaceType({
        projectSpaceTypeId,
        icon: selectedIcon,
        spaceTypeId,
        categoryId,
      })
    );

    if (anchorEl) anchorEl.classList.remove('selected');
    onClose();
    setSelectedIcon(null);
  };

  const handleOnRestore = () => {
    const defaultIcon = anchorEl.attributes.getNamedItem(
      'data-default-icon-name'
    ).value;

    curriedScrollTo(
      0,
      Object.entries(categoryIconsLists).findIndex((categoryIconList) =>
        categoryIconList[1].includes(defaultIcon)
      ) * ICONS_SECTION_HEIGHT
    )();

    setSelectedIcon(defaultIcon);
  };

  return (
    <DraggablePopover
      onEntering={() => {
        if (queuedJump.current) {
          curriedScrollTo(
            0,
            queuedJump.current * ICONS_SECTION_HEIGHT,
            'auto'
          )();
          queuedJump.current = null;
        }
      }}
      anchorEl={anchorEl}
      onClose={() => {
        if (anchorEl) anchorEl.classList.remove('selected');
        onClose();
      }}
      paperProps={{ style: { width: 340 } }}
    >
      <Header>
        <STCButtonsContainer>
          {spaceTypeCategories?.map(
            (spaceTypeCategory: IProjectSpaceTypeCategory, index: number) =>
              CategoryAnchorButton(
                spaceTypeCategory,
                getColor(
                  spaceTypeCategory.defaultSpaceTypeCategory.category,
                  projectColors
                ),
                curriedScrollTo(0, index * ICONS_SECTION_HEIGHT)
              )
          )}
        </STCButtonsContainer>
        <Disclaimer>
          Changing an icon will update it everywhere in this project.
        </Disclaimer>
      </Header>

      <ScrollContent ref={scrollableAreaRef}>
        {spaceTypeCategories?.map((spaceTypeCategory) => (
          <IconSelectionSection
            key={spaceTypeCategory.id}
            spaceTypeCategory={spaceTypeCategory}
            anchorEl={anchorEl}
            selectedIconName={selectedIcon}
            onSelectIcon={(iconName: string) => setSelectedIcon(iconName)}
          />
        ))}
        <div style={{ height: 34 }} />
      </ScrollContent>

      <Footer>
        <SPWMainButton style={{ width: 80 }} onClick={handleOnSave}>
          Save
        </SPWMainButton>

        <SPWOutlineBlackButton
          style={{ marginLeft: 13, width: 80 }}
          onClick={handleOnCancel}
        >
          Cancel
        </SPWOutlineBlackButton>

        <SPWButtonIconAndText
          disabled={disableRestoreBtn}
          style={{ marginLeft: 'auto' }}
          startIcon={
            <img
              alt="restore"
              style={{
                filter: [
                  `grayscale(${disableRestoreBtn ? 1 : 0})`,
                  `brightness(${disableRestoreBtn ? 175 : 100}%)`,
                ].join(' '),
              }}
              src="/images/restore.svg"
            />
          }
          onClick={handleOnRestore}
        >
          Restore
        </SPWButtonIconAndText>
      </Footer>
    </DraggablePopover>
  );
};

export default ImageSelectorPopover;
